// src/pages/TherapistPage/CaseHistory/HistoryPanel.js
import React, { useCallback, useEffect, useState } from 'react';
import {
    countersign, getCompletion, getConsentForCase, getFormSchema, getSections,
    getTriage, requestWithdrawal,
} from './caseFileApi';

const answerText = (question, value) => {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (Array.isArray(value)) {
        const labels = (question?.options || []).filter((o) => value.includes(o.key)).map((o) => o.en);
        return labels.length ? labels.join(', ') : value.join(', ');
    }
    if (typeof value === 'object') {
        if (value.present) return `${value.present.length} sounds marked`;
        return Object.entries(value).map(([k, v]) => `${k}: ${v}`).join(' · ');
    }
    const option = (question?.options || []).find((o) => o.key === value);
    return option ? option.en : String(value);
};

/** The submitted interview, read-only, plus the two things a therapist does to it. */
export default function HistoryPanel({ caseId, onChanged }) {
    const [schema, setSchema] = useState(null);
    const [sections, setSections] = useState([]);
    const [completion, setCompletion] = useState(null);
    const [triage, setTriage] = useState(null);
    const [consent, setConsent] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');

    const load = useCallback(() => {
        Promise.all([
            getFormSchema(), getSections(caseId), getCompletion(caseId),
            getTriage(caseId), getConsentForCase(caseId),
        ])
            .then(([s, sec, comp, tri, con]) => {
                setSchema(s.data); setSections(sec.data); setCompletion(comp.data);
                setTriage(tri.data); setConsent(con.data.find((r) => r.is_live) || null);
                setError('');
            })
            .catch(() => setError('Could not load this case.'));
    }, [caseId]);

    useEffect(() => { load(); }, [load]);

    if (error) return <div className="cf-note red">{error}</div>;
    if (!schema || !completion) return <div className="cf-empty">Loading…</div>;

    const answersFor = (key) => (sections.find((s) => s.section_key === key) || {}).answers || {};

    const doCountersign = async () => {
        setBusy(true);
        try {
            await countersign(caseId);
            setMessage('Interview marked as read. Recording and scoring can go ahead.');
            load(); onChanged && onChanged();
        } catch (err) {
            setError(err.response?.data?.detail || 'Could not countersign.');
        } finally { setBusy(false); }
    };

    const doWithdrawal = async () => {
        const reason = window.prompt('Why is the guardian withdrawing consent?');
        if (!reason || !reason.trim()) return;
        setBusy(true);
        try {
            await requestWithdrawal(consent.id, reason.trim());
            setMessage('Withdrawal requested. Recording is blocked from now; an admin approves the deletion.');
            load();
        } catch (err) {
            setError(err.response?.data?.detail || 'Could not request the withdrawal.');
        } finally { setBusy(false); }
    };

    return (
        <div>
            {message && <div className="cf-note green">{message}</div>}

            {triage && !triage.hearing.satisfied && (
                <div className="cf-note red">
                    <b>Hearing screen missing.</b> {triage.hearing.blocked_reason}
                    <div className="cf-actions">
                        <span className="cf-hint">
                            Record the screen in section 6 of the form, or override it from the
                            Clinical tab.
                        </span>
                    </div>
                </div>
            )}
            {triage?.hearing.overridden && (
                <div className="cf-note amber">
                    <b>Proceeding without a hearing screen.</b> {triage.hearing.override_reason}
                </div>
            )}

            {consent ? (
                <div className="cf-note blue">
                    <b>Consent</b> {consent.text_version} · signed {new Date(consent.signed_at).toLocaleDateString()} by {consent.signer_name} ·
                    audio {consent.permission_audio_clinical ? '✓' : '✗'} ·
                    research {consent.permission_audio_research && consent.research_is_live ? '✓' : '✗'} ·
                    video {consent.permission_video_clinical ? '✓' : '✗'}
                    <div className="cf-actions">
                        <button className="btn btn-secondary" disabled={busy} onClick={doWithdrawal}>
                            Request consent withdrawal…
                        </button>
                    </div>
                </div>
            ) : (
                <div className="cf-note amber">No signed consent on file for this child yet.</div>
            )}

            <div className="cf-bar">
                <span>
                    <b>Interview</b> — {completion.sections.filter((s) => s.owner === 'intake' && s.complete).length}
                    {' of '}{completion.sections.filter((s) => s.owner === 'intake').length} sections complete
                </span>
                {completion.status === 'submitted' && (
                    <>
                        <span className="cf-hint">
                            You are confirming you have read the intake answers. Nothing is locked —
                            the interview can still be corrected.
                        </span>
                        <button className="btn btn-primary" disabled={busy} onClick={doCountersign}>
                            Countersign intake
                        </button>
                    </>
                )}
                {triage?.countersigned_at && (
                    <span className="cf-hint">
                        Countersigned by {triage.countersigned_by_name} on {new Date(triage.countersigned_at).toLocaleDateString()}
                    </span>
                )}
            </div>

            <div className="cf-sections">
                {schema.sections.filter((s) => s.owner === 'intake').map((section) => {
                    const answers = answersFor(section.key);
                    const status = completion.sections.find((s) => s.key === section.key) || {};
                    const answered = section.questions
                        .map((q) => [q, answerText(q, answers[q.key])])
                        .filter(([, text]) => text !== null);
                    return (
                        <details key={section.key} className="cf-section" open={section.key === '4'}>
                            <summary>
                                <span>Section {section.key} · {section.title.en}</span>
                                <span className={`cf-status ${status.complete ? 'locked' : ''}`}>
                                    {status.is_na ? 'N/A' : `${status.answered}/${status.required}`}
                                </span>
                            </summary>
                            {answered.length === 0 ? (
                                <div className="cf-empty">Not answered yet.</div>
                            ) : (
                                <div className="cf-answers">
                                    {answered.map(([q, text]) => (
                                        <div className="cf-answer" key={q.key}>
                                            <span className="q">{q.label.en}</span>
                                            <span className={`a ${/[ऀ-ॿ]/.test(text) ? 'hi' : ''}`}>{text}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </details>
                    );
                })}
            </div>
        </div>
    );
}
